<template>
  <div class="page">
    <div class="page-header">
      <h1 class="page-title">Prize Summary</h1>

      <breadcrumb name="PRIZE_SUMMARY_STATUS"></breadcrumb>

      <div class="page-header-right">
        Date Issued
        <date-picker
          v-model="fromTo"
          type="date"
          value-type="format"
          format="YYYY-MM-DD"
          range
          class="mx-3"
          placeholder="Select date range"
        ></date-picker>
      </div>
      <div class="page-header-actions"></div>
    </div>

    <div class="page-content page-content-table">
      <div class="container-fluid">
        <div class="row">
          <div class="col-12">
            <label
              v-for="filter in filters"
              :key="filter.status"
              class="btn btn-round mr-5"
              :class="{
                'btn-primary': filter.active,
                'btn-default': !filter.active,
              }"
            >
              {{ filter.status | capitalize }}
              <input
                type="checkbox"
                v-model="filter.active"
                @change="fetch"
                style="visibility: hidden; width: 0px;"
              />
            </label>
          </div>
        </div>
      </div>

      <div class="container-fluid">
        <div class="panel">
          <div class="panel-body p-0">
            <vue-good-table
              ref="table"
              mode="remote"
              :rows="list"
              :columns="columns"
              :totalRows="totalRecords"
              :sort-options="sortOption"
              :search-options="searchOption"
              :pagination-options="pagination"
              @on-search="onSearch"
              @on-sort-change="onSortChange"
              @on-page-change="onPageChange"
              @on-per-page-change="onPerPageChange"
              @on-row-click="goToDetail"
            >
              <div slot="table-actions">
                <div class="Excel">
                  <button
                    @click="exportToExcel"
                    class="btn btn-default"
                    :disabled="!canExport"
                  >
                    <i class="fa fa-file-excel-o"></i>
                    Export to Excel
                  </button>
                </div>
              </div>
            </vue-good-table>
          </div>
        </div>
      </div>
    </div>
    <modal name="detail-modal" :width="1000" height="auto">
      <DetailModal :detail="detail" @delivered="updateDetail" />
    </modal>
  </div>
</template>

<script>
import moment from "moment";

import { prizeService } from "@/api/client";
import { tableColumns } from "@/views/prize/status/model";
import vueGoodTableMixins from "@/mixins/vue-good-table";

import DetailModal from "@/views/prize/status/Detail.vue";

export default {
  name: "PrizeStatus",

  mixins: [vueGoodTableMixins],

  components: { DetailModal },

  data() {
    return {
      detail: null,

      list: [],
      fromTo: [],
      download: [],
      columns: tableColumns,

      filters: [
        {
          active: true,
          status: "claimed",
        },
        {
          active: true,
          status: "unclaimed",
        },
        {
          active: false,
          status: "used",
        },
        {
          active: false,
          status: "delivered",
        },
        {
          active: false,
          status: "expired",
        },
      ],
    };
  },

  watch: {
    async fromTo() {
      await this.onPeriodChange();
    },

    async "$route.query"() {
      await this.fetch();
      this.initialTableState();
    },
  },

  beforeMount() {
    this.initialTableState();
  },

  mounted() {
    this.initialTableSearchState();
  },

  computed: {
    canExport() {
      const from = this.fromTo[0];
      const to = this.fromTo[1];
      return from && moment(from).isValid() && to && moment(to).isValid();
    },
  },

  methods: {
    getParams() {
      let params = {
        from: this.fromTo[0],
        to: this.fromTo[1],
      };

      const { sortBy, sortDir, perPage, page, searchTerm } = this.$route.query;

      const pageSize = perPage || this.pagination.perPage;

      if (sortBy) {
        params.order_by = sortBy;
      }

      if (sortDir) {
        params.desc = sortDir == "desc" ? 1 : 0;
      }

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (page) {
        params.start = page ? (page - 1) * pageSize : 0;
      }

      params.count = pageSize || 10;

      return params;
    },

    isValidPeriod() {
      const { from, to } = this.$route.query;

      if (!from || !moment(from).isValid()) {
        return false;
      }

      if (!to || !moment(to).isValid()) {
        return false;
      }

      return true;
    },

    async fetch() {
      if (!this.isValidPeriod()) {
        return;
      }

      const params = this.getParams();

      const filter = this.filters.reduce((v, { status, active }) => {
        return { ...v, [status]: active === true ? 1 : 0 };
      }, {});

      const { data, total_records } = await prizeService.fetchPrizeStatus(
        Object.assign({}, params, filter)
      );

      this.list = data;
      this.totalRecords = total_records;
    },

    async exportToExcel() {
      try {
        const response = await prizeService.prizeStatusDownload({
          from: this.fromTo[0],
          to: this.fromTo[1],
        });

        const blob = new Blob([response.data], {
          type: "application/vnd.ms-excel",
        });

        let a = document.createElement("a");
        a.href = window.URL.createObjectURL(blob);
        a.download = `export_prize_summary_status_${new Date().getTime()}.xlsx`;
        a.click();
      } catch (error) {
        console.log(error);
      }
    },

    async goToDetail(event) {
      const id = event.row.id;
      this.detail = await prizeService.getPrizeDetail(id);

      this.$modal.show(`detail-modal`);
    },

    async updateDetail(id) {
      this.detail = await prizeService.getPrizeDetail(id);
    },

    onPeriodChange() {
      this.$router
        .push({
          query: Object.assign({}, this.$route.query, {
            page: 1,
            from: this.fromTo[0],
            to: this.fromTo[1],
          }),
        })
        .catch((err) => {});
    },
  },
};
</script>

<style scoped>
.page-header-right {
  position: absolute;
  top: 40px;
  bottom: 0;
  right: 30px;
}
.page-header-actions {
  margin-top: 40px;
  position: absolute;
  bottom: 20px;
}
.panel {
  margin-top: 3px;
}
.show_entries {
  margin-top: 20px;
}
.Excel {
  margin: auto 1rem;
}
</style>
